// ═══════════════════════════════════════════════════════════════
// COHÉRENCE PROFIL EMPLOYÉ — source unique CP → { statut, dmfaCode }
// ───────────────────────────────────────────────────────────────
// Problème résolu : cp / statut / dmfaCode étaient 3 champs libres et
// découplés dans toute l'app → on pouvait enregistrer un employé
// incohérent (ex. CP 225.02 = employé, mais marqué statut=ouvrier +
// dmfaCode=497), ce qui faisait appliquer le barème ouvrier (ONSS 108%
// + ONVA) → fiche fausse (dossier Nourdin, 07/2026).
//
// Cette fonction dérive LE bon statut et LA bonne catégorie ONSS à
// partir de la commission paritaire, en s'appuyant sur le type officiel
// de chaque CP (ALL_CP_BELGES : 'O' ouvrier / 'E' employé / 'M' mixte /
// 'S' spécial — source ONSS Annexe 26 2026 + SPF ETCS).
//
// Règle : on ne force QUE l'univoque (E et O). Les CP mixtes (302 Horeca,
// 322 intérim, 330 santé…) dépendent du poste, pas de la CP → non forcées.
// Un dirigeant est hors dérivation CP (peut être dans n'importe quelle CP).
// ═══════════════════════════════════════════════════════════════
import { ALL_CP_BELGES } from './all-cp-belges';

// Catégories ONSS DmfA par défaut : employé = 495, ouvrier = 015.
// ⚠️ CORRIGÉ LE 07/08/2026 — ce fichier dérivait 497 pour tout ouvrier. 497 est
// un code travailleur VALIDE, mais il désigne les « Gardiens ou gardiennes
// d'enfants reconnus », pas un ouvrier ordinaire. ✅ Annexe 2 des instructions
// ONSS (« Liste des codes travailleurs pour lesquels des cotisations sont
// dues », version 2024/2), lue au texte :
//     « 015  Ouvriers et assimilés, y compris le personnel de maison
//            — Ouvriers ordinaires »
//     « 495  Travailleurs intellectuels et assimilés, employés de maison… »
//     « 497  Gardiens ou gardiennes d'enfants reconnus »
// La DmfA d'un ouvrier partait donc avec le code des gardiens d'enfants.
const DMFA_EMPLOYE = '495';
const DMFA_OUVRIER = '015';
// Exceptions curatées (dmfaCode ≠ défaut). Aligné sur CP_PRESETS_FULL
// (app/pages/employees.js). Ex. CP 307 (courtiers d'assurances) = 490.
const DMFA_EXCEPTIONS = { '307': '490' };

// mainCP('200.01') -> '200'
function mainCP(cp) { return String(cp || '').trim().split('.')[0]; }

// Recherche l'entrée CP (exacte puis CP principale).
function lookupCP(cp) {
  const exact = String(cp || '').trim();
  return ALL_CP_BELGES[exact] || ALL_CP_BELGES[mainCP(exact)] || null;
}

// categoriePourCP(cp) -> { statut, dmfaCode } | null si CP inconnue / mixte / spéciale.
// null = « on ne peut pas trancher depuis la CP seule » → ne rien forcer.
export function categoriePourCP(cp) {
  const entry = lookupCP(cp);
  if (!entry) return null;
  const dmfaEx = DMFA_EXCEPTIONS[mainCP(cp)];
  if (entry.type === 'E') return { statut: 'employe', dmfaCode: dmfaEx || DMFA_EMPLOYE };
  if (entry.type === 'O') return { statut: 'ouvrier', dmfaCode: dmfaEx || DMFA_OUVRIER };
  return null; // 'M' (mixte) ou 'S' (spécial) : ambigu, non forcé
}

// coherenceProfil({ cp, statut, dmfaCode }) : vérifie/aligne un profil.
// Retourne { statut, dmfaCode, corrected, coherent, reason } :
//  - corrected=true si on a dû aligner (le profil entrant était incohérent) ;
//  - les statut/dmfaCode renvoyés sont TOUJOURS ceux à persister.
// N'altère jamais un dirigeant ni une CP mixte/inconnue (préserve l'entrant).
export function coherenceProfil({ cp, statut, dmfaCode } = {}) {
  if (statut === 'dirigeant') {
    return { statut, dmfaCode, corrected: false, coherent: true, reason: 'dirigeant — hors dérivation CP' };
  }
  const cat = categoriePourCP(cp);
  if (!cat) {
    return { statut, dmfaCode, corrected: false, coherent: true, reason: 'CP inconnue/mixte — non forcée' };
  }
  const statutIn = statut || '';
  const dmfaIn = dmfaCode || '';
  const corrected = (statutIn && statutIn !== cat.statut) || (dmfaIn && dmfaIn !== cat.dmfaCode);
  return {
    statut: cat.statut,
    dmfaCode: cat.dmfaCode,
    corrected: !!corrected,
    coherent: !corrected,
    reason: corrected ? `CP ${cp} => ${cat.statut}/${cat.dmfaCode} (aligné)` : 'cohérent',
  };
}
